// useConvictionTimeseries.ts
// Fetches conviction timeseries from GET /api/signals/{market_id}/conviction
// for one or more groups in parallel.
//
// Supports two modes:
//   Bucketed  — resolution: '1m' | '5m' | '15m' | '1h'  (LOCF, regular grid)
//   Raw       — resolution: 'raw'  (every stored row, variable cadence)

import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';

// ---------------------------------------------------------------------------
// Types — mirror ConvictionTimePoint / ConvictionTimeseriesResponse in api_signals.py
// ---------------------------------------------------------------------------

export interface ConvictionTimePoint {
    ts: Date;
    raw_implied: number;
    direction: 'YES' | 'NO' | null;
    n_traders_yes: number;
    n_traders_no: number;
    capital_usd_yes: number;
    capital_usd_no: number;
    open_shares_yes: number;
    open_shares_no: number;
    // Derived convenience fields (computed client-side)
    net_capital: number;    // capital_usd_yes - capital_usd_no
    net_traders: number;    // n_traders_yes   - n_traders_no
}

export interface ConvictionSeries {
    marketId: string;
    group: string;
    points: ConvictionTimePoint[];
    isRaw: boolean;
}

/** 'raw' bypasses time_bucket and returns every stored row. */
export type ConvictionResolution = '1m' | '5m' | '15m' | '1h' | 'raw';

export interface ConvictionTimeseriesResult {
    /** Map from group name → parsed + enriched series */
    byGroup: Map<string, ConvictionSeries>;
    loading: boolean;
    error: Error | null;
}

// ---------------------------------------------------------------------------
// Raw API response shape
// ---------------------------------------------------------------------------

interface RawPoint {
    ts: string;
    raw_implied: number;
    direction: string | null;
    n_traders_yes: number;
    n_traders_no: number;
    capital_usd_yes: number;
    capital_usd_no: number;
    open_shares_yes: number;
    open_shares_no: number;
}

interface RawResponse {
    market_id: string;
    group_name: string;
    points: RawPoint[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useConvictionTimeseries(
    marketId: string | null,
    resolution: ConvictionResolution = '1h',
    groups: string[] = ['insider'],
): ConvictionTimeseriesResult {
    const [byGroup, setByGroup] = useState<Map<string, ConvictionSeries>>(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Track the latest request so stale responses from previous markets are dropped
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!marketId || groups.length === 0) {
            setByGroup(new Map());
            setLoading(false);
            setError(null);
            return;
        }

        // Cancel any in-flight requests for a previous market / resolution
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);

        const isRaw = resolution === 'raw';

        const promises = groups.map((group) => {
            const params = new URLSearchParams({
                group,
                resolution,
                limit: isRaw ? '5000' : '2000',
            });
            return apiGet<RawResponse>(
                `/api/signals/${encodeURIComponent(marketId)}/conviction?${params}`,
                ctrl.signal
            ).then((raw): [string, ConvictionSeries] => {
                const points: ConvictionTimePoint[] = raw.points.map((p) => ({
                    ts: new Date(p.ts),
                    raw_implied: p.raw_implied,
                    direction: (p.direction as ConvictionTimePoint['direction']) ?? null,
                    n_traders_yes: p.n_traders_yes,
                    n_traders_no: p.n_traders_no,
                    capital_usd_yes: p.capital_usd_yes,
                    capital_usd_no: p.capital_usd_no,
                    open_shares_yes: p.open_shares_yes,
                    open_shares_no: p.open_shares_no,
                    net_capital: p.capital_usd_yes - p.capital_usd_no,
                    net_traders: p.n_traders_yes - p.n_traders_no,
                }));
                return [group, { marketId: raw.market_id, group, points, isRaw }];
            });
        });

        Promise.all(promises)
            .then((results) => {
                if (ctrl.signal.aborted) return;
                setByGroup(new Map(results));
                setLoading(false);
            })
            .catch((err: Error) => {
                if (ctrl.signal.aborted || err?.name === 'AbortError') return;
                console.error('[useConvictionTimeseries] fetch failed:', err);
                setError(err);
                setLoading(false);
            });

        return () => {
            ctrl.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketId, resolution, groups.join(',')]);

    return { byGroup, loading, error };
}